import React, { useState, useEffect } from 'react';
import { Check, Minus, AlertTriangle, Copy, CheckCheck, ChevronDown, ChevronRight, Bell, Key, Loader2, Eye, EyeOff } from 'lucide-react';

// Steps that produce credentials — when marked done, prompt to save
const CREDENTIAL_STEPS = {
  hw_filevault:           { fields: [{ name: 'password', label: 'Recovery Key', placeholder: 'XXXX-XXXX-XXXX-XXXX-XXXX-XXXX' }], integration: 'FileVault' },
  preflight_apple_id:     { fields: [{ name: 'username', label: 'Email', placeholder: 'agent@icloud.com' }, { name: 'password', label: 'Password', placeholder: 'Password', secret: true }], integration: 'Apple ID' },
  preflight_anthropic_key:{ fields: [{ name: 'api_key', label: 'API Key', placeholder: 'sk-ant-...', secret: true }], integration: 'Anthropic' },
  preflight_brave_key:    { fields: [{ name: 'api_key', label: 'API Key', placeholder: 'BSA...', secret: true }], integration: 'Brave Search' },
  preflight_telegram_bot: { fields: [{ name: 'api_key', label: 'Bot Token', placeholder: '1234567890:ABCdef...', secret: true }], integration: 'Telegram Bot' },
  preflight_agent_email:  { fields: [{ name: 'username', label: 'Email', placeholder: 'agent@domain.com' }, { name: 'password', label: 'Password', placeholder: 'Password', secret: true }], integration: 'Agent Email' },
  sec_gateway_auth:       { fields: [{ name: 'api_key', label: 'Gateway Token', placeholder: 'Token value', secret: true }], integration: 'Gateway Auth' },
};

const STATUS_STYLES = {
  done:    'text-emerald-400',
  na:      'text-[#F0EFF4]/30',
  blocked: 'text-red-400',
  undone:  'text-[#F0EFF4]/40',
};

const BuildStepCheckbox = ({
  step,
  state,      // { status, note, blocked_reason, completed_at, notified_at } or null
  onAction,   // fn(stepId, action, extra)
  onNotify,   // fn(step)
  disabled,
  clientId,   // for credential save
  password: adminPassword, // for credential save API
}) => {
  const [showNote, setShowNote] = useState(false);
  const [noteText, setNoteText] = useState(state?.note || '');
  const [copied, setCopied] = useState(false);
  const [blockedReason, setBlockedReason] = useState('');
  const [showBlockForm, setShowBlockForm] = useState(false);
  const [showCredPrompt, setShowCredPrompt] = useState(false);
  const [credValues, setCredValues] = useState({});
  const [credSaving, setCredSaving] = useState(false);
  const [credSaved, setCredSaved] = useState(false);
  const [credRevealed, setCredRevealed] = useState({});

  const credConfig = CREDENTIAL_STEPS[step.id];

  const status = state?.status || 'undone';
  const isDone = status === 'done';
  const isNA = status === 'na';
  const isBlocked = status === 'blocked';

  const handleCheck = () => {
    if (disabled) return;
    if (isDone) {
      onAction(step.id, 'undone');
      setShowCredPrompt(false);
      setCredSaved(false);
    } else {
      onAction(step.id, 'done');
      // Show credential prompt if this step produces credentials
      if (credConfig && clientId) {
        setShowCredPrompt(true);
        setCredSaved(false);
      }
    }
  };

  const saveCredential = async () => {
    if (!credConfig || !clientId) return;
    const hasValue = Object.values(credValues).some(v => v && v.trim());
    if (!hasValue) { setShowCredPrompt(false); return; }

    setCredSaving(true);
    try {
      const res = await fetch('/api/save-credential-value', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-admin-password': adminPassword },
        body: JSON.stringify({
          client_id: clientId,
          integration_name: credConfig.integration,
          ...credValues,
        }),
      });
      if (!res.ok) throw new Error('Save failed');
      setCredSaved(true);
      setTimeout(() => setShowCredPrompt(false), 1500);
    } catch (err) {
      alert(`Credential save failed: ${err.message}`);
    } finally {
      setCredSaving(false);
    }
  };

  const handleNA = (e) => {
    e.stopPropagation();
    if (disabled) return;
    if (isNA) onAction(step.id, 'undone');
    else onAction(step.id, 'na');
  };

  const handleBlock = () => {
    if (disabled) return;
    if (isBlocked) { onAction(step.id, 'unblock'); return; }
    setShowBlockForm(v => !v);
  };

  const submitBlock = () => {
    onAction(step.id, 'blocked', { blocked_reason: blockedReason });
    setShowBlockForm(false);
    setBlockedReason('');
  };

  const copyCommand = async () => {
    if (!step.command) return;
    await navigator.clipboard.writeText(step.command);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const saveNote = () => {
    onAction(step.id, isDone ? 'done' : 'undone', { note: noteText });
    setShowNote(false);
  };

  const rowBg = isBlocked
    ? 'bg-red-500/5 border-red-500/20'
    : isDone
    ? 'bg-emerald-500/5 border-emerald-500/10'
    : isNA
    ? 'bg-[#F0EFF4]/5 border-[#F0EFF4]/5'
    : 'bg-transparent border-transparent';

  return (
    <div className={`rounded-lg border px-3 py-2 transition-all ${rowBg} ${step.critical ? 'ring-1 ring-red-500/30' : ''}`}>
      <div className="flex items-start gap-2">
        {/* Checkbox */}
        <button
          onClick={handleCheck}
          disabled={disabled || isBlocked}
          className={`mt-0.5 w-4 h-4 shrink-0 rounded border flex items-center justify-center transition-colors ${
            isDone
              ? 'bg-emerald-500 border-emerald-500'
              : isBlocked
              ? 'bg-red-500/20 border-red-500/50 cursor-not-allowed'
              : 'border-[#F0EFF4]/30 hover:border-[#7B61FF] bg-transparent'
          }`}
        >
          {isDone && <Check className="w-2.5 h-2.5 text-white" />}
          {isBlocked && <AlertTriangle className="w-2.5 h-2.5 text-red-400" />}
        </button>

        {/* Label + meta */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className={`text-sm ${isDone ? 'text-[#F0EFF4]/40 line-through' : isNA ? 'text-[#F0EFF4]/30 line-through' : isBlocked ? 'text-red-400' : 'text-[#F0EFF4]'} ${step.critical ? 'font-bold' : ''}`}>
              {step.label}
            </span>
            {step.critical && (
              <span className="text-[10px] font-data font-bold text-red-400 bg-red-500/10 px-1.5 py-0.5 rounded">CRITICAL</span>
            )}
            {step.required && (
              <span className="text-[10px] font-data text-yellow-400 bg-yellow-500/10 px-1.5 py-0.5 rounded">REQUIRED</span>
            )}
            {isBlocked && (
              <span className="text-[10px] font-data text-red-400">• {state?.blocked_reason || 'Blocked'}</span>
            )}
            {state?.notified_at && (
              <Bell className="w-3 h-3 text-blue-400 shrink-0" />
            )}
          </div>

          {/* Command */}
          {step.command && (
            <div className="mt-1 flex items-center gap-1">
              <code className="text-[11px] font-data text-[#7B61FF] bg-[#7B61FF]/10 px-1.5 py-0.5 rounded truncate max-w-xs">
                {step.command}
              </code>
              <button
                onClick={copyCommand}
                className="shrink-0 p-0.5 rounded hover:bg-[#F0EFF4]/10 text-[#F0EFF4]/40 hover:text-[#F0EFF4] transition-colors"
              >
                {copied ? <CheckCheck className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
              </button>
            </div>
          )}

          {/* Note preview */}
          {state?.note && !showNote && (
            <div className="mt-1 text-[11px] text-[#F0EFF4]/40 italic truncate">{state.note}</div>
          )}

          {/* Note editor */}
          {showNote && (
            <div className="mt-1.5 flex items-center gap-1">
              <input
                value={noteText}
                onChange={e => setNoteText(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && saveNote()}
                placeholder="Add note…"
                className="flex-1 text-xs bg-[#F0EFF4]/5 border border-[#F0EFF4]/10 rounded px-2 py-1 text-[#F0EFF4] placeholder-[#F0EFF4]/20 outline-none focus:border-[#7B61FF]/50"
              />
              <button onClick={saveNote} className="text-xs px-2 py-1 bg-[#7B61FF] hover:bg-[#6B51EF] text-white rounded transition-colors">Save</button>
              <button onClick={() => setShowNote(false)} className="text-xs px-2 py-1 bg-[#F0EFF4]/10 text-[#F0EFF4]/60 rounded">✕</button>
            </div>
          )}

          {/* Credential prompt */}
          {showCredPrompt && credConfig && !credSaved && (
            <div className="mt-2 p-2.5 bg-[#7B61FF]/5 border border-[#7B61FF]/20 rounded-lg">
              <div className="flex items-center gap-1.5 mb-2">
                <Key className="w-3 h-3 text-[#7B61FF]" />
                <span className="text-[11px] font-data font-bold text-[#7B61FF]">Save {credConfig.integration} credential?</span>
              </div>
              <div className="space-y-1.5">
                {credConfig.fields.map(field => (
                  <div key={field.name} className="flex items-center gap-1">
                    <span className="text-[10px] text-[#F0EFF4]/40 font-data w-16 shrink-0">{field.label}</span>
                    <div className="relative flex-1">
                      <input
                        type={field.secret && !credRevealed[field.name] ? 'password' : 'text'}
                        value={credValues[field.name] || ''}
                        onChange={e => setCredValues(v => ({ ...v, [field.name]: e.target.value }))}
                        onKeyDown={e => e.key === 'Enter' && saveCredential()}
                        placeholder={field.placeholder}
                        className="w-full text-xs bg-[#0A0A14] border border-[#F0EFF4]/10 rounded px-2 py-1 pr-7 text-[#F0EFF4] placeholder-[#F0EFF4]/15 outline-none focus:border-[#7B61FF]/50 font-data"
                      />
                      {field.secret && (
                        <button
                          type="button"
                          onClick={() => setCredRevealed(v => ({ ...v, [field.name]: !v[field.name] }))}
                          className="absolute right-1.5 top-1/2 -translate-y-1/2 text-[#F0EFF4]/20 hover:text-[#F0EFF4]/50"
                        >
                          {credRevealed[field.name] ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-1.5 mt-2">
                <button
                  onClick={saveCredential}
                  disabled={credSaving}
                  className="flex items-center gap-1 text-xs px-2.5 py-1 bg-[#7B61FF] hover:bg-[#6B51EF] text-white rounded font-semibold transition-colors disabled:opacity-40"
                >
                  {credSaving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Key className="w-3 h-3" />}
                  Save
                </button>
                <button
                  onClick={() => setShowCredPrompt(false)}
                  className="text-xs px-2 py-1 text-[#F0EFF4]/30 hover:text-[#F0EFF4]/60 transition-colors"
                >
                  Skip
                </button>
                <span className="text-[10px] text-[#F0EFF4]/20 ml-auto">Stored securely in credential_values</span>
              </div>
            </div>
          )}
          {showCredPrompt && credSaved && (
            <div className="mt-2 p-2 bg-emerald-500/10 border border-emerald-500/20 rounded-lg flex items-center gap-2">
              <CheckCheck className="w-3.5 h-3.5 text-emerald-400" />
              <span className="text-xs text-emerald-400 font-data">
                {credConfig.integration} credential saved ✓
              </span>
            </div>
          )}

          {/* Block form */}
          {showBlockForm && (
            <div className="mt-1.5 flex items-center gap-1">
              <input
                value={blockedReason}
                onChange={e => setBlockedReason(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && submitBlock()}
                placeholder="Why is this blocked?"
                className="flex-1 text-xs bg-red-500/5 border border-red-500/20 rounded px-2 py-1 text-[#F0EFF4] placeholder-[#F0EFF4]/20 outline-none"
              />
              <button onClick={submitBlock} className="text-xs px-2 py-1 bg-red-500/20 hover:bg-red-500/40 text-red-400 rounded">Block</button>
              <button onClick={() => setShowBlockForm(false)} className="text-xs px-2 py-1 bg-[#F0EFF4]/10 text-[#F0EFF4]/60 rounded">✕</button>
            </div>
          )}
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-0.5 shrink-0">
          {/* N/A */}
          <button
            onClick={handleNA}
            disabled={disabled}
            title={isNA ? 'Mark required' : 'Mark N/A'}
            className={`text-[10px] font-data px-1.5 py-0.5 rounded transition-colors ${
              isNA
                ? 'bg-[#F0EFF4]/20 text-[#F0EFF4]/60'
                : 'bg-[#F0EFF4]/5 text-[#F0EFF4]/30 hover:bg-[#F0EFF4]/10 hover:text-[#F0EFF4]/60'
            }`}
          >
            N/A
          </button>
          {/* Block */}
          <button
            onClick={handleBlock}
            disabled={disabled}
            title={isBlocked ? 'Unblock' : 'Mark blocked'}
            className={`p-1 rounded transition-colors ${
              isBlocked
                ? 'text-red-400 bg-red-500/10'
                : 'text-[#F0EFF4]/20 hover:text-red-400 hover:bg-red-500/10'
            }`}
          >
            <AlertTriangle className="w-3 h-3" />
          </button>
          {/* Note */}
          <button
            onClick={() => setShowNote(v => !v)}
            title="Add note"
            className="p-1 rounded text-[#F0EFF4]/20 hover:text-[#7B61FF] hover:bg-[#7B61FF]/10 transition-colors"
          >
            <ChevronDown className={`w-3 h-3 transition-transform ${showNote ? 'rotate-180' : ''}`} />
          </button>
          {/* Notify */}
          {onNotify && (
            <button
              onClick={() => onNotify(step)}
              title="Notify client"
              className="p-1 rounded text-[#F0EFF4]/20 hover:text-blue-400 hover:bg-blue-500/10 transition-colors"
            >
              <Bell className="w-3 h-3" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default BuildStepCheckbox;
